import { cn } from '@/lib/utils';
import type { ArticleSummary } from '@/content/articles';

export function ArticleArtwork({
  article,
  className,
  priority = false,
}: {
  article: Pick<ArticleSummary, 'category' | 'image' | 'imageAlt'>;
  className?: string;
  priority?: boolean;
}) {
  if (!article.image) {
    return (
      <DefaultArticleArtwork
        category={article.category}
        {...(className ? { className } : {})}
      />
    );
  }
  const variant =
    article.category === 'Comparisons'
      ? 'comparison'
      : article.category === 'Guides'
        ? 'guide'
        : 'infrastructure';
  return (
    <div className={cn('article-art', `article-art--${variant}`, className)}>
      <div className="article-art-grid" aria-hidden="true" />
      <img
        src={article.image}
        alt={article.imageAlt}
        width={1200}
        height={720}
        loading={priority ? 'eager' : 'lazy'}
        fetchPriority={priority ? 'high' : 'auto'}
        decoding="async"
        className="article-art-svg article-art-image"
      />
    </div>
  );
}

/** A local draft can still be previewed before its final artwork is supplied. */
function DefaultArticleArtwork({
  category,
  className,
}: {
  category: string;
  className?: string;
}) {
  const variant =
    category === 'Comparisons'
      ? 'comparison'
      : category === 'Guides'
        ? 'guide'
        : 'infrastructure';

  return (
    <div
      className={cn('article-art', `article-art--${variant}`, className)}
      aria-hidden="true"
    >
      <div className="article-art-grid" />
      <svg viewBox="0 0 600 360" fill="none" className="article-art-svg">
        {variant === 'comparison' ? (
          <>
            <path d="M0 180h600M300 0v360" stroke="currentColor" opacity=".1" />
            <circle
              cx="300"
              cy="180"
              r="134"
              stroke="currentColor"
              opacity=".16"
            />
            <circle
              cx="300"
              cy="180"
              r="171"
              stroke="currentColor"
              opacity=".08"
            />
            <g transform="translate(136 86) rotate(-10 83 94)">
              <rect
                width="166"
                height="188"
                rx="13"
                fill="#223b3a"
                stroke="#7a9b94"
              />
              <rect
                x="17"
                y="17"
                width="132"
                height="154"
                rx="5"
                stroke="#7a9b94"
                opacity=".45"
              />
              <path
                d="M83 54 113 71v36L83 124l-30-17V71l30-17Z"
                stroke="#bfd3c9"
                strokeWidth="1.5"
              />
              <path
                d="m53 71 30 18 30-18M83 89v35"
                stroke="#bfd3c9"
                strokeWidth="1.5"
              />
              <path
                d="M55 145h56"
                stroke="#7a9b94"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </g>
            <g transform="translate(300 80) rotate(10 83 94)">
              <rect
                width="166"
                height="188"
                rx="13"
                fill="#dbe6db"
                stroke="#f1f7eb"
              />
              <rect
                x="17"
                y="17"
                width="132"
                height="154"
                rx="5"
                stroke="#93afa0"
                opacity=".65"
              />
              <circle
                cx="83"
                cy="88"
                r="30"
                stroke="#456e62"
                strokeWidth="1.5"
              />
              <circle
                cx="83"
                cy="88"
                r="19"
                stroke="#456e62"
                strokeWidth="1.5"
              />
              <path
                d="M55 145h56"
                stroke="#93afa0"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </g>
            <circle cx="302" cy="182" r="24" fill="#ff7836" stroke="#ffb68b" />
            <path d="M292 178h20m-20 8h20" stroke="#40291e" strokeWidth="2" />
          </>
        ) : variant === 'guide' ? (
          <>
            <circle
              cx="300"
              cy="180"
              r="140"
              stroke="currentColor"
              opacity=".16"
            />
            <circle
              cx="300"
              cy="180"
              r="178"
              stroke="currentColor"
              opacity=".08"
            />
            <path
              d="m300 155 136 73-136 73-136-73 136-73Z"
              fill="#ece2d3"
              stroke="#baa58e"
            />
            <path
              d="m300 112 136 73-136 73-136-73 136-73Z"
              fill="#f7f1e7"
              stroke="#baa58e"
            />
            <path
              d="m300 69 136 73-136 73-136-73 136-73Z"
              fill="#fffaf1"
              stroke="#bda98f"
            />
            <path d="m300 93 92 49-92 49-92-49 92-49Z" stroke="#d1bda4" />
            <path
              d="m300 116 48 26-48 26-48-26 48-26Z"
              fill="#ef733f"
              stroke="#cb5d32"
            />
            <path
              d="M300 217v84m136-159v86M164 142v86"
              stroke="#bda98f"
              strokeDasharray="3 5"
            />
            <circle cx="164" cy="228" r="4" fill="#9b8064" />
            <circle cx="436" cy="228" r="4" fill="#9b8064" />
          </>
        ) : (
          <>
            <circle
              cx="300"
              cy="180"
              r="122"
              stroke="currentColor"
              opacity=".13"
            />
            <circle
              cx="300"
              cy="180"
              r="163"
              stroke="currentColor"
              opacity=".09"
            />
            <path
              d="M183 113h63l54 67m117-67h-63l-54 67m-117 67h63l54-67m117 67h-63l-54-67M300 58v122m0 122V180"
              stroke="#7a9caa"
              strokeWidth="1.5"
            />
            {(
              [
                [158, 88],
                [392, 88],
                [158, 222],
                [392, 222],
              ] as const
            ).map(([x, y]) => (
              <g key={`${x}-${y}`}>
                <rect
                  x={x}
                  y={y}
                  width="50"
                  height="50"
                  rx="8"
                  fill="#edf3f4"
                  stroke="#a0b4bd"
                />
                <rect
                  x={x + 15}
                  y={y + 15}
                  width="20"
                  height="20"
                  rx="4"
                  stroke="#698794"
                />
              </g>
            ))}
            <rect
              x="255"
              y="135"
              width="90"
              height="90"
              rx="15"
              fill="#294a5a"
              stroke="#779eac"
            />
            <rect
              x="268"
              y="148"
              width="64"
              height="64"
              rx="8"
              stroke="#86a8b1"
            />
            <path
              d="m284 177 11 11 22-22"
              stroke="#e0f0e7"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx="300" cy="58" r="6" fill="#ec8151" />
            <circle cx="300" cy="302" r="6" fill="#ec8151" />
          </>
        )}
      </svg>
      <div className="article-art-caption">
        <span>DEN / PERSPECTIVES</span>
        <span>{category.toUpperCase()}</span>
      </div>
    </div>
  );
}
